import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Check, AlertTriangle } from 'react-feather';

import { Text, TEXT_VARIANTS } from 'ui/Typography';
import trackEvent from 'misc/trackEvent';
import Recorder from 'ui/MediaRecorder/Recorder';
import { useFeatureFlags } from 'components/shared/hooks';
import MediaPlayer from 'components/ui/MediaPlayer';
import RetryButton from 'components/Practice/shared/RetryButton';
import SkippedOption from 'components/Practice/Recording/SkippedOption';
import UniversalPlayer from 'components/shared/UniversalPlayer';

const MediaResponseRecorder = props => {
  const [tryAgainDisabled, setTryAgainDisabled] = useState(false);

  const {
    givenAnswer,
    errorMessage,
    skipRecording,
    finished,
    loadRecording,
    previewing,
    questionId,
    removeAbilityToSkip,
    showAnswerAlreadySubmitted,
    apiKey,
    saveMedia,
    recordWebcamEvent,
    setUploadFinished,
    setUploadStarted,
    handleBadUploadType,
    webcamPlaybackEnabled,
    recording,
    i18n,
    sendFeedbackError,
    selectedTab,
  } = props;

  const seismicRecordingEnabled = useFeatureFlags(
    'readiness-enable-seismic-recording',
  );

  const tryAgainClicked = () => {
    setTryAgainDisabled(true);
    loadRecording();
    trackEvent('practice/recording_try_again_clicked', {
      content_type: 'question',
      content_id: questionId,
    });
  };
  const recorderStyles = classNames('VideoEmbed RecordingQuestion-player', {
    'VideoEmbed--disabled': finished && !givenAnswer,
  });
  const previewingStyles = classNames({ 'VideoEmbed--disabled': previewing });
  const showSkipOption = !finished && !removeAbilityToSkip;
  const showReportTechnicalDifficulties = removeAbilityToSkip;
  const skipDisabled = previewing || finished;

  // This can be simplified once we are exclusively on the new skip experience
  const skipOrSendFeedbackOption = () => {
    if (
      !sendFeedbackError &&
      (showSkipOption || showReportTechnicalDifficulties)
    ) {
      return (
        <SkippedOption
          skipRecording={skipRecording}
          disabled={skipDisabled}
          showTechnicalDifficulties
          i18n={i18n}
        />
      );
    }
    return null;
  };

  const recordButtonText = () => {
    const text = seismicRecordingEnabled
      ? t('questions.question.webcam_recording.record')
      : null;
    return text;
  };

  if (selectedTab === 'TAB_RESPONSES') {
    return null;
  }
  if (givenAnswer) {
    return (
      <div className="VideoEmbed-player">
        {recording.provider === 'ziggeo' && (
          <MediaPlayer
            hasZiggeoForAllMedia
            recording={recording}
            ziggeoApiKey={apiKey}
          />
        )}
        {recording.provider === 'seismic' && (
          <UniversalPlayer id={recording.id} videoType="Recording" isPractice />
        )}
        {!finished && (
          <div className="Video-redo d-f fw-w jc-sb ai-c pos-r">
            <Text
              className="d-f ai-c"
              variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD_SUCCESS}
            >
              <Check className="mr-4" />
              {t('questions.question.webcam_recording.recording_complete')}
            </Text>
            <RetryButton
              onClick={tryAgainClicked}
              disabled={tryAgainDisabled}
              setTryAgainDisabled={setTryAgainDisabled}
            />
          </div>
        )}
        {showAnswerAlreadySubmitted && (
          <Text className="d-f ai-c pull-right" variant={TEXT_VARIANTS.BODY}>
            {t('questions.question.media_recording.response_already_submitted')}
          </Text>
        )}
      </div>
    );
  }

  return (
    <div className={recorderStyles}>
      {!errorMessage && (
        <div className={previewingStyles}>
          <Recorder
            apiKey={apiKey}
            saveMedia={saveMedia}
            recordWebcamEvent={recordWebcamEvent}
            setUploadingComplete={setUploadFinished}
            setUploadStarted={setUploadStarted}
            handleBadUploadType={handleBadUploadType}
            webcamPlaybackEnabled={webcamPlaybackEnabled}
            seismicRecordingEnabled={seismicRecordingEnabled}
            recordButtonText={recordButtonText()}
          />
        </div>
      )}
      {skipOrSendFeedbackOption()}
      {errorMessage && (
        <div className="ErrorContainer bg-transparent ff-ih d-f ai-c mb-0 p-0 mt-8 pos-r">
          <AlertTriangle className="mr-8" />
          <div className="f-1">
            <Text variant={TEXT_VARIANTS.INSTRUX_ERROR}>
              {`${t(
                'questions.question.video_response.error',
              )} ${errorMessage}. `}
              {`${t('questions.question.video_response.error_follow_up')} ${t(
                'common.or',
              )} `}
            </Text>
            <button
              type="button"
              className="u-linkBtn d-ib c-p"
              onClick={loadRecording}
            >
              <Text variant={TEXT_VARIANTS.BODY_LINK}>
                {t('questions.question.video_response.try_again')}
              </Text>
            </button>
            .
          </div>
        </div>
      )}
    </div>
  );
};

MediaResponseRecorder.propTypes = {
  givenAnswer: PropTypes.string,
  finished: PropTypes.bool,
  i18n: PropTypes.shape({}),
  errorMessage: PropTypes.string,
  loadRecording: PropTypes.func,
  saveMedia: PropTypes.func,
  apiKey: PropTypes.string,
  skipRecording: PropTypes.func,
  previewing: PropTypes.bool,
  questionId: PropTypes.number,
  webcamPlaybackEnabled: PropTypes.bool,
  recordWebcamEvent: PropTypes.func,
  removeAbilityToSkip: PropTypes.bool,
  setUploadFinished: PropTypes.func,
  setUploadStarted: PropTypes.func,
  handleBadUploadType: PropTypes.func,
  recording: PropTypes.shape({}),
};

export default MediaResponseRecorder;
